import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Observable, Subject, debounceTime, switchMap, catchError, map, of, takeUntil } from 'rxjs';
import { AccountsService } from '../accounts.service';
import { AccountingTaxCode } from '../accounting-tax-code.model';
import { AccountingDocumentAction, DocumentAuditEvent } from '../accounting-lifecycle.model';
import { AccountingWorkflowActionsComponent } from '../shared/accounting-workflow-actions.component';
import { AccountingAuditTimelineComponent } from '../shared/accounting-audit-timeline.component';
import { AccountingAttachmentsPanelComponent } from '../shared/accounting-attachments-panel.component';
import { AccountingDocumentAttachment } from '../accounting-attachment.model';
import { ChartOfAccountsService } from '../../chart-of-accounts/chart-of-accounts.service';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { CRM_PAYABLE_ROLES, CRM_RECEIVABLE_ROLES } from '../../shared/crm-client-contact-picker/crm-party-context.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { NewFinanceNote, NewFinanceNoteAction, NewFinanceNoteDraft, NewFinanceNoteKind, NewFinanceNoteSummary, VendorClaimEvidence, VendorClaimEvidenceDraft, SupplierCreditClaimReview } from './new-finance-note.model';
import { NewFinanceNoteService } from './new-finance-note.service';

interface SourceOption { id: number; label: string; currency?: string; }
type ClientOption = CrmClient & { displayName: string };

@Component({
    selector: 'app-new-finance-notes', standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, AutoCompleteModule, ButtonModule, InputGroupModule,
        InputGroupAddonModule, InputNumberModule, InputTextModule, IftaLabelModule, SelectModule, TableModule,
        ToastModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent, AccountingWorkflowActionsComponent,
        AccountingAuditTimelineComponent, AccountingAttachmentsPanelComponent],
    providers: [MessageService], templateUrl: './new-finance-notes.component.html', styleUrls: ['./new-finance-notes.component.css']
})
export class NewFinanceNotesComponent implements OnInit, OnDestroy {
    kind!: NewFinanceNoteKind;
    mode!: 'list' | 'add' | 'edit' | 'view';
    companyId = 0;
    note?: NewFinanceNote;
    auditEvents: DocumentAuditEvent[] = [];
    evidence: VendorClaimEvidence[] = [];
    evidenceRejectionReasons: Record<number, string> = {};
    overlapReviews: SupplierCreditClaimReview[] = [];
    overlapReasons: Record<number, string> = {};
    evidenceReversalReasons: Record<number, string> = {};
    attachments: AccountingDocumentAttachment[] = [];
    evidenceDraft?: VendorClaimEvidenceDraft;
    readonly eligibilityOptions = [{ label: 'Recoverable', value: true }, { label: 'Not recoverable', value: false }];
    rows: NewFinanceNoteSummary[] = [];
    clients: ClientOption[] = [];
    selectedClient: ClientOption | null = null;
    sources: SourceOption[] = [];
    selectedSource: SourceOption | null = null;
    accounts: { label: string; value: number }[] = [];
    taxCodes: AccountingTaxCode[] = [];
    searchText = '';
    page = 0;
    busy = false;
    draft!: NewFinanceNoteDraft;
    private readonly clientSearch = new Subject<string>();
    private readonly sourceSearch = new Subject<string>();
    private readonly destroyed = new Subject<void>();

    constructor(private route: ActivatedRoute, private router: Router, private notes: NewFinanceNoteService,
        private accountsService: AccountsService, private chart: ChartOfAccountsService, private crm: CrmService,
        private session: SessionStorageService, private permissions: PermissionService, private messages: MessageService) {}

    get customer(): boolean { return this.kind === 'customer-credit-notes'; }
    get title(): string { return this.customer ? 'Customer Credit Notes' : 'Vendor Debit Claims'; }
    get base(): string { return this.customer ? '/accounts/enterprise/receivables/customer-credit-note' : '/accounts/enterprise/payables/vendor-debit-note'; }
    get prefix(): string { return this.customer ? 'accounts.customer-credit-note.' : 'accounts.vendor-debit-note.'; }
    can(action: string): boolean { return this.permissions.getPermissionCodes().has(this.prefix + action); }
    get allowedActions(): AccountingDocumentAction[] {
        const actions: AccountingDocumentAction[] = [];
        if (this.can('edit')) actions.push('submit');
        if (this.can('approve')) actions.push('approve', 'reject');
        if (this.can('post')) actions.push('post');
        if (this.can('cancel')) actions.push('cancel');
        if (this.can('reverse')) actions.push('reverse');
        return actions;
    }

    ngOnInit(): void {
        this.kind = this.route.snapshot.data['kind']; this.mode = this.route.snapshot.data['mode'];
        this.companyId = this.session.getCompany()?.id || 0;
        const now = new Date();
        const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        this.draft = { owningCompanyId: this.companyId, crmClientId: 0, noteDate: localDate,
            currencyCode: 'SAR', reason: '', lines: [{ description: '', amount: 0 }] };
        if (!this.companyId) { this.error('Select a company first.'); return; }
        this.clientSearch.pipe(debounceTime(250), switchMap(text => this.crm.searchClients({ companyId: this.companyId,
            searchText: text, roles: this.customer ? CRM_RECEIVABLE_ROLES : CRM_PAYABLE_ROLES, size: 20 })
            .pipe(catchError(() => { this.error('Could not search parties.'); return of({ content: [], totalElements: 0 }); }))), takeUntil(this.destroyed))
            .subscribe(result => this.clients = result.content.map(client => this.clientOption(client)));
        this.sourceSearch.pipe(debounceTime(250), switchMap(text => this.searchSourceRequest(text)), takeUntil(this.destroyed))
            .subscribe(items => this.sources = items);
        if (this.mode === 'list') this.loadList();
        else {
            if (this.mode !== 'view') this.loadOptions();
            const id = Number(this.route.snapshot.paramMap.get('id'));
            if (id) this.load(id);
            else this.restoreSourceFromRoute();
        }
    }

    ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }

    loadList(): void {
        this.busy = true;
        this.notes.list(this.kind, this.companyId, this.page, 25, undefined, undefined, this.searchText).subscribe({
            next: rows => { this.rows = rows; this.busy = false; },
            error: err => { this.busy = false; this.error(this.message(err, 'Could not load notes.')); }
        });
    }
    load(id: number): void {
        this.busy = true;
        this.notes.get(this.kind, id).subscribe({ next: note => {
            this.note = note; this.draft = { ...note, lines: note.lines.map(line => ({ ...line })) }; this.busy = false;
            if (this.mode === 'view') this.loadAudit(note.noteId);
            if (this.mode === 'view' && !this.customer) {
                this.loadEvidence(); this.loadAttachments(); this.loadOverlapReviews();
                this.evidenceDraft = { supplierReference: '', supplierDate: '', currencyCode: note.currencyCode,
                    attachmentId: null, lines: note.lines.filter(line => line.lineId != null).map(line => ({
                        claimLineId: line.lineId!, netAmount: null, taxAmount: null, taxCode: line.taxCode || '', inputVatRecoverable: null
                    })) };
            }
            if (this.mode === 'edit') {
                this.crm.getClient(note.crmClientId).subscribe({ next: result => this.selectedClient = this.clientOption(result.data),
                    error: () => this.error('Could not load the note party.') });
                if (note.sourceDocumentId) this.loadSource(note.sourceDocumentId);
            }
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not load the note.')); } });
    }
    private loadOptions(): void {
        this.chart.getAllChartOfAccountsByCompany(this.companyId, false).subscribe({ next: rows => {
            const types = this.customer ? ['revenue', 'income'] : ['expense', 'asset'];
            this.accounts = rows.filter(a => a.isActive && !a.deprecated && types.includes((a.internalType || '').toLowerCase()))
                .map(a => ({ label: `${a.accountCode} - ${a.accountName}`, value: a.accountId }));
        }, error: () => this.error('Could not load accounts.') });
        this.accountsService.getAccountingTaxCodes(this.companyId).subscribe({ next: rows => this.taxCodes = rows,
            error: () => this.error('Could not load tax codes.') });
    }
    searchClients(event: { query: string }): void { this.clientSearch.next(event.query || ''); }
    selectClient(client: ClientOption): void {
        this.selectedClient = client; this.draft.crmClientId = client.id || 0; this.draft.crmClientContactId = null;
        this.selectedSource = null; this.draft.sourceDocumentId = null; this.draft.sourceDocumentReference = null;
        this.sources = []; this.sourceSearch.next('');
    }
    clearClient(): void { this.selectedClient = null; this.draft.crmClientId = 0; this.selectedSource = null; this.draft.sourceDocumentId = null;
        this.draft.sourceDocumentReference = null; this.sources = []; this.sourceSearch.next(''); }
    searchSources(event: { query: string }): void { this.sourceSearch.next(event.query || ''); }
    private searchSourceRequest(text: string): Observable<SourceOption[]> {
        if (!this.draft.crmClientId) return of([] as SourceOption[]);
        const partyId = this.draft.crmClientId;
        const result: Observable<{ items: any[] }> = this.customer
            ? this.accountsService.getGeneratedInvoices(0, 20, this.companyId, [this.draft.crmClientId], [], undefined, undefined, text)
            : this.accountsService.getGeneratedBills(0, 20, this.companyId, [this.draft.crmClientId], undefined, undefined, undefined, undefined, undefined, undefined, text);
        return result.pipe(map(response => partyId !== this.draft.crmClientId ? [] : response.items.map((item: any) => ({ id: this.customer ? item.invoiceId : item.billId,
            label: this.customer ? item.invoiceNumber : item.billNumber, currency: item.country?.currencyCode })) as SourceOption[]),
            catchError(() => { this.error('Could not search source documents.'); return of([] as SourceOption[]); }));
    }
    selectSource(source: SourceOption): void { this.selectedSource = source; this.draft.sourceDocumentId = source.id;
        this.draft.sourceDocumentReference = source.label; if (source.currency) this.draft.currencyCode = source.currency; }
    clearSource(): void { this.selectedSource = null; this.draft.sourceDocumentId = null; this.draft.sourceDocumentReference = null; }
    private restoreSourceFromRoute(): void {
        const id = Number(this.route.snapshot.queryParamMap.get(this.customer ? 'originalInvoiceId' : 'originalBillId'));
        if (id) this.loadSource(id);
    }
    private loadSource(id: number): void {
        const request: Observable<any> = this.customer ? this.accountsService.getInvoiceById(id) : this.accountsService.getBillById(id);
        request.subscribe({ next: (item: any) => {
            if ((item.owningCompany?.id ?? item.owningCompanyId) !== this.companyId) { this.error('The source belongs to another company.'); return; }
            this.selectSource({ id, label: this.customer ? item.invoiceNumber : item.billNumber, currency: item.country?.currencyCode });
            if (!this.draft.crmClientId && item.crmClientId) {
                this.crm.getClient(item.crmClientId).subscribe({ next: result => { this.selectedClient = this.clientOption(result.data); this.draft.crmClientId = item.crmClientId; },
                    error: () => this.error('Could not load the source party.') });
            }
        }, error: () => this.error('Could not load the source document.') });
    }
    save(): void {
        if (this.busy) return;
        if (this.mode === 'edit' && (!this.note || this.note.documentStatus !== 'DRAFT' || this.note.postingStatus === 'POSTED')) {
            this.error('Only an unposted draft can be edited.'); return;
        }
        if (!this.draft.crmClientId || this.selectedClient?.id !== this.draft.crmClientId
            || this.draft.sourceDocumentId && this.selectedSource?.id !== this.draft.sourceDocumentId
            || !this.draft.noteDate || !this.draft.currencyCode?.trim() || !this.draft.reason?.trim()
            || !this.draft.lines.length || this.draft.lines.some(line => !line.description?.trim() || !line.amount || line.amount <= 0)) {
            this.error('Select a party and provide a date, currency, reason and positive line amounts.'); return;
        }
        this.busy = true;
        const request = this.mode === 'edit' && this.note ? this.notes.update(this.kind, this.note.noteId, this.draft) : this.notes.create(this.kind, this.draft);
        request.subscribe({ next: note => { this.busy = false; this.router.navigate([this.base, 'view', note.noteId]); },
            error: err => { this.busy = false; this.error(this.message(err, 'Could not save the note.')); } });
    }
    workflow(action: AccountingDocumentAction, reason?: string): void {
        if (this.busy || !this.note || !this.allowedActions.includes(action)) return;
        this.busy = true;
        this.notes.workflow(this.kind, this.note.noteId, action as NewFinanceNoteAction, reason).subscribe({
            next: note => { this.note = note; this.busy = false; this.loadAudit(note.noteId); this.loadOverlapReviews(); }, error: err => { this.busy = false; this.error(this.message(err, 'The action failed.')); }
        });
    }
    private loadAudit(id: number): void {
        this.notes.auditEvents(this.kind, id).subscribe({ next: events => this.auditEvents = events,
            error: () => this.error('Could not load note activity.') });
    }
    loadEvidence(): void {
        if (!this.note || this.customer) return;
        this.notes.supplierEvidence(this.note.noteId).subscribe({ next: rows => this.evidence = rows,
            error: err => this.error(this.message(err, 'Could not load supplier evidence.')) });
    }
    loadOverlapReviews(): void {
        this.overlapReviews = [];
        if (!this.note || this.customer || this.note.documentStatus !== 'APPROVED'
            || !this.permissions.getPermissionCodes().has('accounts.credit-note.view')) return;
        this.notes.claimCreditReviews(this.note.noteId).subscribe({ next: rows => this.overlapReviews = rows,
            error: err => this.error(this.message(err, 'Could not load related vendor credits.')) });
    }
    confirmUnrelated(item: SupplierCreditClaimReview): void {
        if (this.busy || !this.note || this.customer || !this.can('approve') || item.reviewed) return;
        const reason = this.overlapReasons[item.id]?.trim();
        if (!reason) { this.error('Enter why the credit and claim are unrelated.'); return; }
        this.busy = true;
        this.notes.confirmCreditClaimUnrelated(item.id, this.note.noteId, reason).subscribe({ next: () => {
            this.busy = false; delete this.overlapReasons[item.id]; this.loadOverlapReviews(); this.loadAudit(this.note!.noteId);
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not record the review.')); } });
    }
    verifyEvidence(item: VendorClaimEvidence): void {
        if (this.busy || !this.note || this.customer || !this.can('post') || !this.can('approve')
            || this.note.documentStatus !== 'POSTED' || !['MATCHED', 'PARTIAL'].includes(item.matchStatus)) return;
        this.busy = true;
        this.notes.verifySupplierEvidence(this.note.noteId, item.evidenceId).subscribe({ next: () => {
            this.busy = false; this.loadEvidence(); this.loadAudit(this.note!.noteId);
            this.messages.add({ severity: 'success', summary: 'Evidence verified', detail: 'Claim suspense was cleared for this evidence.' });
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not verify the evidence.')); } });
    }
    reverseEvidence(item: VendorClaimEvidence): void {
        if (this.busy || !this.note || this.customer || !this.can('reverse') || item.matchStatus !== 'VERIFIED') return;
        const reason = this.evidenceReversalReasons[item.evidenceId]?.trim();
        if (!reason) { this.error('Enter a reason for reversing this evidence.'); return; }
        this.busy = true;
        this.notes.reverseSupplierEvidence(this.note.noteId, item.evidenceId, reason).subscribe({ next: () => {
            this.busy = false; delete this.evidenceReversalReasons[item.evidenceId]; this.loadEvidence(); this.loadAudit(this.note!.noteId);
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not reverse the evidence.')); } });
    }
    claimLineDescription(id: number): string {
        return this.note?.lines.find(line => line.lineId === id)?.description || `Line #${id}`;
    }
    rejectEvidence(item: VendorClaimEvidence): void {
        if (this.busy || !this.note || this.customer || !this.can('edit') || this.note.documentStatus !== 'POSTED'
            || this.note.postingStatus !== 'POSTED' || !['MATCHED', 'PARTIAL', 'REVIEW'].includes(item.matchStatus)) return;
        const reason = this.evidenceRejectionReasons[item.evidenceId]?.trim();
        if (!reason || reason.length > 1000) { this.error('Enter a rejection reason of at most 1000 characters.'); return; }
        this.busy = true;
        this.notes.rejectSupplierEvidence(this.note.noteId, item.evidenceId, reason).subscribe({ next: () => {
            this.busy = false; delete this.evidenceRejectionReasons[item.evidenceId];
            this.loadEvidence(); this.loadAudit(this.note!.noteId);
            this.messages.add({ severity: 'success', summary: 'Evidence rejected', detail: 'You can record corrected evidence.' });
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not reject the evidence.')); } });
    }
    loadAttachments(): void {
        if (!this.note || this.customer) return;
        this.accountsService.getAccountingDocumentAttachments('VENDOR_DEBIT_NOTE', this.note.noteId).subscribe({
            next: rows => this.attachments = rows, error: err => this.error(this.message(err, 'Could not load attachments.')) });
    }
    saveEvidence(): void {
        if (this.busy || !this.note || this.customer || this.note.documentStatus !== 'POSTED'
            || this.note.postingStatus !== 'POSTED' || !this.evidenceDraft || !this.can('edit')) return;
        const lines = this.evidenceDraft.lines.filter(line => line.netAmount != null && line.netAmount > 0)
            .map(line => ({ ...line }));
        if (!this.evidenceDraft.supplierReference.trim() || !this.evidenceDraft.supplierDate || !this.evidenceDraft.attachmentId
            || !lines.length || lines.some(line => line.taxAmount == null || line.taxAmount < 0 || !line.taxCode)) {
            this.error('Provide the supplier reference, date, attachment and at least one complete evidence line.'); return;
        }
        this.busy = true;
        this.notes.addSupplierEvidence(this.note.noteId, { ...this.evidenceDraft, lines }).subscribe({ next: () => {
            this.busy = false; this.evidenceDraft!.supplierReference = ''; this.evidenceDraft!.attachmentId = null;
            this.evidenceDraft!.lines.forEach(line => { line.netAmount = null; line.taxAmount = null; line.inputVatRecoverable = null; });
            this.loadEvidence(); this.loadAudit(this.note!.noteId);
            this.messages.add({ severity: 'success', summary: 'Evidence recorded', detail: 'Review the matching status before verification.' });
        }, error: err => { this.busy = false; this.error(this.message(err, 'Could not record supplier evidence.')); } });
    }
    private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'Finance note', detail }); }
    private message(error: any, fallback: string): string { return error?.error?.message || error?.error?.detail || fallback; }
    private clientOption(client: CrmClient): ClientOption {
        return { ...client, displayName: client.companyName || client.customerNameLocal || client.clientNumber || `Party #${client.id}` };
    }
}
